<script lang="ts" setup>
import { computed, reactive, ref, PropType, watch } from 'vue';
import { useRouter } from 'vue-router';
import { openRouterLink } from '@/utils/router-utils';
import { formatDateTime, deepParseDates } from '@/utils/date-utils';
import {
  ComputeTaskUtils,
  DataPipelineTaskTabOptionType,
  DATA_PIPELINE_TASK_TAB_OPTIONS,
} from '@/utils/compute-utils';
import { highlightJson } from '@/utils/prism-utils';
import ComputeTaskDebugDetails from '@/components/compute/ComputeTaskDebugDetails.vue';
import TaskResultState from '@/components/compute/TaskResultState.vue';
import { useProjectTaskQuery } from '@/composables/project-task-polling';
import { BucketType, ComputeTaskType, DataObjectType } from '@/gql/graphql';
import {
  BAlert,
  BButton,
  BButtonGroup,
  BCard,
  BModal,
  BPagination,
  BSpinner,
} from 'bootstrap-vue-next';
import {
  COMPUTE_TASK_STATUS,
  COMPUTE_TASK_TYPES,
  IS_DEBUG_MODE,
  PAGING_ITEMS_PER_PAGE,
} from '@/constants';
import ComputeTaskDetail, {
  TaskFileType,
} from '@/components/compute/ComputeTaskDetail.vue';
import ComputeTaskAbort from '@/components/compute/ComputeTaskAbort.vue';
import { useUserStore } from '@/store/user';
import { getFileDownloadUrl } from '@/utils/fileService';
import DataObjectDetail from '@/components/data/DataObjectDetail.vue';
import ComputeTaskFilesList from '@/components/compute/ComputeTaskFilesList.vue';
import ComputeTaskStatusBadge from '@/components/compute/ComputeTaskStatusBadge.vue';
import { getRootTasks, pageRootTasks } from '@/utils/compute-task-root-paging';

const router = useRouter();
const userStore = useUserStore();

// --- Component Properties ---
const props = defineProps({
  projectId: { type: Number as PropType<number | null>, default: null },
  taskTypes: { type: Array as PropType<string[] | null>, default: null },
  // Exclude parents by child task type.
  excludeSubtaskTypes: {
    type: Array as PropType<string[] | null>,
    default: null,
  },
  // paging specification
  pagingItemsPerPage: { type: Number, default: PAGING_ITEMS_PER_PAGE },
  // additinal component props
  messageNoItemsFound: {
    type: String,
    default: 'No compute tasks found.',
  },
  taskNameColumnTitle: {
    type: String,
    default: 'Task Name',
  },
  showExecDetails: {
    type: Boolean,
    default: true,
  },
});

// --- Set minimum card width ---
const colMinWidth = 450;

// --- Component State ---
const disableTransitionConfig = ref<'none' | 'multi' | 'all'>('multi'); // none, multi (multiple cards only), all (disable single card animations too)
const disableTransitionsState = ref(disableTransitionConfig.value === 'all');
const selectedTask = ref<ComputeTaskType | null>(null);
const selectedDataObject = ref<DataObjectType | null>(null);
const showModalDataObjectDetail = ref(false);
const pagingPageCurrent = ref<number>(1);
const pagingTotalRows = computed(() => {
  return tasksQueryResult.value?.computeTasks?.total || 0;
});
const pagerTotalRows = computed(() => rootTasks.value.length);

// Tab selection for tasks & subtasks
// get helper with safe default
const selectedTabByTaskId = reactive<
  Record<number, DataPipelineTaskTabOptionType>
>({});

function getSelectedTab(id?: number | null) {
  if (!id) {
    return DATA_PIPELINE_TASK_TAB_OPTIONS.FILES;
  }
  const selectedTab = selectedTabByTaskId[id];
  if (selectedTab) {
    return selectedTab;
  }
  // otherwise check whether the task is root or subtask and set default tab accordingly
  const task = tasks.value.find((t) => t.id === id);
  if (task && task.parentId) {
    // subtask - default to progress
    return DATA_PIPELINE_TASK_TAB_OPTIONS.PROGRESS;
  }

  // otherwise consider root task - default to files
  return DATA_PIPELINE_TASK_TAB_OPTIONS.FILES;
}

function setSelectedTab(
  id?: number | null,
  val: DataPipelineTaskTabOptionType = DATA_PIPELINE_TASK_TAB_OPTIONS.FILES
) {
  if (id) {
    selectedTabByTaskId[id] = val;
  }
}

// set selected tab to task and all subtasks
function setSelectedTabToAll(
  rootTask: ComputeTaskType,
  selectedTab: DataPipelineTaskTabOptionType
) {
  setSelectedTab(rootTask.id, selectedTab);
  rootTask.subtasks?.forEach((st) => {
    if (st?.id != null) {
      setSelectedTab(st.id, selectedTab);
    }
  });
}

// Expanded/Collapsed tasks & subtasks
// get helper with safe default
const expandedByTaskId = reactive<Record<number, boolean>>({});

function getExpanded(id?: number | null) {
  if (!id) {
    return false;
  }
  // if id not in the map, default to false unless root task with no parent and multiple subtasks
  if (expandedByTaskId[id] === undefined) {
    const task = rootTasks.value.find(
      (t) => t.id === id && t.taskType === COMPUTE_TASK_TYPES.COMPUTE_PIPELINE
    );
    if (task) {
      // root compute pipeline task - default to expanded view if files are available
      return !!(task.results.filesAvailable || task.results.files?.length);
    } else {
      // otherwise default to collapsed
      return false;
    }
  }
  // otherwise return the value
  return expandedByTaskId[id];
}

// write helper used by child event
function setExpanded(id?: number | null, val = false) {
  if (id) {
    expandedByTaskId[id] = val;
    return setTaskExpanded(id, val);
  }
}

// expand/collapse all subtasks for a given root task
function setExpandToAll(rootTask: ComputeTaskType, expand: boolean) {
  // disable transitions temporarily to avoid janky animations when expanding/collapsing multiple cards
  disableTransitionsState.value = disableTransitionConfig.value !== 'none';
  // expand/collapse root task and all subtasks
  setExpanded(rootTask.id, expand);
  rootTask.subtasks?.forEach((st) => {
    if (st?.id != null) {
      setExpanded(st.id, expand);
    }
  });
  // reset transitions state to default after a short delay to allow for layout changes
  setTimeout(() => {
    disableTransitionsState.value = disableTransitionConfig.value === 'all';
  }, 250);
}

// --- Compute Tasks List ---
const tasks = computed<ComputeTaskType[]>(() => {
  return (
    tasksQueryResult.value?.computeTasks?.computeTasks
      ?.filter((task) => task && task !== null)
      .map((task) => ({
        ...(deepParseDates(task) as ComputeTaskType),
      })) || []
  );
});

// --- Compute Task Hierarchy ---
const rootTasks = computed(() => getRootTasks(tasks.value));
const visibleRootTasks = computed(() =>
  pageRootTasks(
    rootTasks.value,
    pagingPageCurrent.value,
    props.pagingItemsPerPage
  )
);

// --- Component state ---

const noTasks = computed(() => {
  return tasks.value.length === 0;
});

// --- Query Compute Tasks ---
const {
  result: tasksQueryResult,
  loading: tasksQueryInProgress,
  error: tasksQueryError,
  refetch: refetchTasks,
  onResult: onTasksQueryDone,
  onError: onTasksQueryError,
  detailErrors,
  setVisibleTasks,
  setExpanded: setTaskExpanded,
} = useProjectTaskQuery(
  computed(() => ({
    projectId: props.projectId,
    taskTypes: props.taskTypes,
    excludeSubtaskTypes: props.excludeSubtaskTypes,
    // date & time filters
    // paging specification: all rows, so a parent and its children stay one
    // card; visibleRootTasks pages the cards
    pagingItemsPerPage: 0,
  }))
);

watch(
  visibleRootTasks,
  (rows) => {
    setVisibleTasks(
      rows.flatMap((task) => [
        task.id!,
        ...(task.subtasks || []).map((child) => child!.id!),
      ])
    );
    for (const task of rows)
      if (getExpanded(task.id)) void setTaskExpanded(task.id!, true);
  },
  { immediate: true }
);

watch(pagerTotalRows, (total) => {
  if (props.pagingItemsPerPage > 0) {
    const lastPage = Math.max(1, Math.ceil(total / props.pagingItemsPerPage));
    if (pagingPageCurrent.value > lastPage) pagingPageCurrent.value = lastPage;
  }
});

onTasksQueryDone((res) => {
  if (res.data) {
    if (res.data.computeTasks) {
      console.debug('Tasks retrieved successfuly (ComputeTasksNested)');
    } else {
      console.error('Error, could not retrieve tasks:', res.data);
    }
  } else if (!res.loading) {
    console.error('Error, could not retrieve tasks result data:', res);
  }
});

onTasksQueryError((err) => {
  console.error('Error fetching code (onCodeError):', err.message);
});

function requestRefetchTasks() {
  refetchTasks()
    ?.catch((err) => {
      console.error('Refetch tasks failed:', err);
    })
    .then(() => {
      console.debug('Refetched tasks');
    });
}

function requestSearch() {
  pagingPageCurrent.value = 1;
  requestRefetchTasks();
}

// --- File Requests ---
function filePreviewRequested(task: ComputeTaskType, file: TaskFileType) {
  console.debug('file-preview-request', file);
  selectedTask.value = task;
  selectedDataObject.value = {
    isFolder: false,
    name: file.path,
  } as DataObjectType;
  showModalDataObjectDetail.value = true;
}

async function fileDownloadRequested(
  task: ComputeTaskType,
  file: TaskFileType
) {
  console.debug('file-download-request', file);
  // validate the selected data object
  if (!file.name) {
    console.error('No name found in selected data object');
    return;
  }
  if (!task?.bucket?.id) {
    console.error('Task Bucket ID not found');
    return;
  }
  // get the download URL
  const url = await getFileDownloadUrl(task.bucket.id, file.path);
  // validate the URL
  if (!url) {
    console.error(
      `Unexpected error fetching download URL for the file ${file.path}`
    );
    alert(
      'Failed to generate download link. The requested file might not be available anymore.'
    );
    return;
  }
  // open the URL in a new tab to trigger the download
  window.open(url, '_blank');
}

// --- Compute Task Abort Request ---
const showConfirmAbortModal = ref<boolean>(false);

function taskAbortRequested(task: ComputeTaskType) {
  if (!task?.id) {
    console.error('Task cancellation requested, but task ID is missing');
    return;
  }
  console.debug('Cancel task ', task.id, 'requested, waiting for confirmation');
  selectedTask.value = task;
  showConfirmAbortModal.value = true;
}

function closeTaskAbortModal() {
  showConfirmAbortModal.value = false;
}

// --- Debug Details ---
const debugTaskId = ref<number | null>(null);
const detailsContent = ref<any>(null);
const detailsModalTitle = ref('Compute Task Details');
const showDetailsModal = ref(false);

async function showExecDetails(task: ComputeTaskType) {
  if (task.id) {
    const loaded = await setExpanded(task.id, true);
    if (!loaded) return;
    task = loaded;
  }
  detailsModalTitle.value = task.name || 'Compute Task Exec Details';
  let censoredAttributes = task.attributes;

  // only allow certain attributes to be copied and viewed if not admin/developer
  if (!userStore.admin || !userStore.developer) {
    censoredAttributes = Object.fromEntries(
      Object.entries(task.attributes).filter(([key]) =>
        [
          // common attribues
          'samples', // bcl to fastq conversion + data pipeline
          'version',
          // ftp transfer
          'ftpCmd',
          'autoAnnotate',
          'reconstructData',
          'createProject',
          // data pipeline
          'startPhase',
          'endPhase',
          'bucketOutput',
        ].includes(key)
      )
    );
    // if task.attributes.samples is available, convert the array of objects to array of strings by taking the object's name property only
    if (Array.isArray(censoredAttributes.samples)) {
      censoredAttributes.samples = censoredAttributes.samples.map(
        (sample: { name: string }) => sample.name
      );
    }
  }
  detailsContent.value = censoredAttributes;
  showDetailsModal.value = true;
}

// --- Expose Properties to the Outside ---
defineExpose({
  tasks,
  pagingTotalRows,
  tasksQueryInProgress,
  requestSearch,
});

// --- Manage Cards Layout Changes ---
function onLayoutNeeded(phase: 'start' | 'end' | 'resize', id?: number | null) {
  console.debug('Layout requested, phase: ', phase, 'id:', id);

  // check task ID provided
  if (!id) {
    console.warn('Layout requested but no task ID provided');
    return;
  }

  // identify the container element for the task
  const container = document.querySelector(`#task-${id}`) as HTMLElement | null;

  // check container exists
  if (!container) {
    console.warn(`Layout requested but container #task-${id} not found`);
    return;
  }

  // pause transitions while heights are changing
  if (phase === 'start' && disableTransitionsState.value) {
    console.debug('Pausing transitions for layout');
    container.classList.add('task-no-anim');
  }

  // resume transitions when layout is complete
  if (phase === 'end' && disableTransitionsState.value) {
    requestAnimationFrame(() => {
      console.debug('Resuming transitions after layout');
      container.classList.remove('task-no-anim');
    });
  }
}

// --- Router Helpers for Navigation ---
function bucketRequested(
  bucket: BucketType | undefined | null,
  index?: number,
  evt?: MouseEvent
) {
  if (bucket && bucket.id) {
    // redirect to bucket detail page if permitted
    if (!bucket.deletedAt) {
      openRouterLink(
        {
          name: 'BucketDetail',
          params: { id: bucket.id },
          query: {
            showMetadata: '1',
            allowDownload: '1',
            listComputeTasks: '0',
            listSamples: '0',
            listProjects: '0',
            listUsers: '0',
            returnTo: encodeURIComponent(router.currentRoute.value.fullPath),
            returnLabel: 'Project Detail',
          },
        },
        router,
        evt
      );
    } else {
      alert('The bucket for this task has been deleted.');
    }
  }
}
</script>
<template>
  <div>
    <!-- loading and error alerts -->
    <b-alert v-if="tasksQueryInProgress" variant="info">Loading...</b-alert>
    <b-alert v-if="tasksQueryError" variant="danger">
      {{ tasksQueryError.message }}
      <b-button
        @click="requestRefetchTasks"
        variant="outline-dark"
        class="btn-sm"
      >
        <font-awesome-icon icon="sync" />
        Retry
      </b-button>
    </b-alert>

    <!-- empty list -->
    <b-alert :model-value="noTasks" variant="secondary">
      <span v-if="tasksQueryInProgress">
        Loading tasks <b-spinner small />
      </span>
      <span v-else> {{ props.messageNoItemsFound }} </span>
    </b-alert>

    <!-- List tasks -->
    <template
      v-for="(rootTask, idx) in visibleRootTasks"
      :key="rootTask.id || idx"
    >
      <TaskResultState
        v-if="getExpanded(rootTask.id)"
        :error="detailErrors[rootTask.id!]"
      />
      <!-- 1. No subtasks-->
      <template v-if="rootTask.subtasks?.length === 0">
        <ComputeTaskDetail
          :id="`task-${rootTask.id}`"
          :task="rootTask"
          class="mb-2"
          :expanded="getExpanded(rootTask.id)"
          :selected-tab="getSelectedTab(rootTask.id)"
          @update:expanded="(val) => setExpanded(rootTask.id ?? 0, val)"
          @update:selected-tab="val => setSelectedTab(rootTask.id!, val)"
          @layout-needed="onLayoutNeeded"
          @bucket-requested="bucketRequested(rootTask.bucket, idx, $event)"
          @exec-details-requested="showExecDetails(rootTask)"
          @debug-details-requested="debugTaskId = rootTask.id || null"
          @abort-requested="taskAbortRequested(rootTask)"
          @file-preview-requested="
            (file) => filePreviewRequested(rootTask, file)
          "
          @file-download-requested="
            (file) => fileDownloadRequested(rootTask, file)
          "
        />
      </template>

      <!-- 3. Parent task with subtasks: one card per subtask -->
      <BCard v-else class="mb-2 root-task-card">
        <!-- Task Header: Tile + Metadata + Status | ActionButtons -->
        <div
          class="d-flex flex-column flex-sm-row align-items-start justify-content-between gap-2"
        >
          <!-- Title + Metadata + Status -->
          <div class="me-sm-3 flex-grow-1">
            <h2 class="mb-0">{{ rootTask.name }} (legacy)</h2>
            <!-- Metadata: number of subtasks + date created-->
            <p class="mb-0">
              <small>
                {{ rootTask.subtasks?.length }} subtasks, created
                {{ formatDateTime(rootTask.createdAt) }}
              </small>
            </p>
            <!-- Status / Progress Text Description / Error Message -->
            <ComputeTaskStatusBadge :task="rootTask" />
          </div>

          <!-- Subtask Controls: expand all / collapse all -->
          <div class="mb-2 ms-sm-auto">
            <b-button-group class="me-2 mb-1">
              <b-button
                size="sm"
                variant="outline-secondary"
                v-b-tooltip.hover
                title="Expand all"
                @click="setExpandToAll(rootTask, true)"
                :id="`btn-expand-${rootTask.id}`"
              >
                <i class="bi bi-arrows-expand" />
              </b-button>
              <b-button
                size="sm"
                variant="outline-secondary"
                @click="setExpandToAll(rootTask, false)"
                :id="`btn-collapse-${rootTask.id}`"
                v-b-tooltip.hover
                title="Collapse all"
              >
                <i class="bi bi-arrows-collapse" />
              </b-button>
            </b-button-group>

            <b-button-group class="mb-1">
              <!-- Progress Timeline Link -->
              <b-button
                v-if="rootTask.bucketId || rootTask.bucket"
                size="sm"
                variant="outline-dark"
                @click="
                  setExpanded(rootTask.id, true);
                  setSelectedTabToAll(
                    rootTask,
                    DATA_PIPELINE_TASK_TAB_OPTIONS.PROGRESS
                  );
                "
                :id="`btn-progress-${rootTask.id}`"
                v-b-tooltip.hover
                title="View progress timeline with stats"
                :active="
                  getExpanded(rootTask.id) &&
                  getSelectedTab(rootTask.id) ===
                    DATA_PIPELINE_TASK_TAB_OPTIONS.PROGRESS
                "
              >
                <font-awesome-icon icon="list-check" />
              </b-button>
              <!-- Figures -->
              <b-button
                hidden
                v-if="rootTask.bucketId || rootTask.bucket"
                size="sm"
                variant="outline-dark"
                @click="
                  setExpanded(rootTask.id, true);
                  setSelectedTabToAll(
                    rootTask,
                    DATA_PIPELINE_TASK_TAB_OPTIONS.FIGURES
                  );
                "
                :id="`btn-figures-${rootTask.id}`"
                v-b-tooltip.hover
                title="View figures"
                :active="
                  getExpanded(rootTask.id) &&
                  getSelectedTab(rootTask.id) ===
                    DATA_PIPELINE_TASK_TAB_OPTIONS.FIGURES
                "
              >
                <font-awesome-icon icon="chart-line" />
              </b-button>
              <!-- Files List -->
              <b-button
                v-if="rootTask.bucketId || rootTask.bucket"
                size="sm"
                variant="outline-dark"
                @click="
                  setExpanded(rootTask.id, true);
                  setSelectedTabToAll(
                    rootTask,
                    DATA_PIPELINE_TASK_TAB_OPTIONS.FILES
                  );
                "
                :id="`btn-files-${rootTask.id}`"
                v-b-tooltip.hover
                title="View files"
                :active="
                  getExpanded(rootTask.id) &&
                  getSelectedTab(rootTask.id) ===
                    DATA_PIPELINE_TASK_TAB_OPTIONS.FILES
                "
              >
                <font-awesome-icon icon="file" />
              </b-button>
              <!-- Exec Configuration -->
              <BButton
                variant="outline-dark"
                size="sm"
                @click="showExecDetails(rootTask)"
                :id="`btn-exec-${rootTask.id}`"
                v-b-tooltip.hover
                title="View exec configuration"
              >
                <font-awesome-icon :icon="['fas', 'sliders']" />
              </BButton>
              <!-- Bucket Link for Developers -->
              <BButton
                v-if="IS_DEBUG_MODE || userStore.developer"
                variant="outline-dark"
                size="sm"
                @click="bucketRequested(rootTask.bucket, idx, $event)"
                :id="`btn-bucket-${rootTask.id}`"
                :disabled="!rootTask.bucket"
                v-b-tooltip.hover
                title="Go to data"
              >
                <font-awesome-icon :icon="['fas', 'database']" />
              </BButton>
              <!-- Debug Details -->
              <BButton
                v-if="IS_DEBUG_MODE || userStore.developer"
                variant="outline-dark"
                size="sm"
                @click="debugTaskId = rootTask.id || null"
                :id="`btn-debug-${rootTask.id}`"
                v-b-tooltip.hover
                title="View debug details"
              >
                <font-awesome-icon :icon="['fas', 'code']" />
              </BButton>
              <!-- Abort Compute Task -->
              <b-button
                size="sm"
                variant="outline-danger"
                :disabled="!ComputeTaskUtils.canBeAborted(rootTask.status)"
                @click="taskAbortRequested(rootTask)"
                :id="`btn-abort-${rootTask.id}`"
                v-b-tooltip.hover
                title="Abort task"
                :active="rootTask.status === COMPUTE_TASK_STATUS.ABORTING"
              >
                <font-awesome-icon icon="times" />
              </b-button>
            </b-button-group>
          </div>
          <!-- end of action buttons group div -->
        </div>
        <!-- end of task header row -->

        <!-- Expanded Root Task Details -->
        <div v-if="getExpanded(rootTask.id)" class="mb-2">
          <!-- Show Root Task Progress -->
          <div
            hidden
            v-if="
              getSelectedTab(rootTask.id) ===
              DATA_PIPELINE_TASK_TAB_OPTIONS.PROGRESS
            "
          >
            <hr />
            <small>Not yet implemented</small>
          </div>

          <!-- List Root Task Files -->
          <div
            v-if="
              getSelectedTab(rootTask.id) ===
              DATA_PIPELINE_TASK_TAB_OPTIONS.FILES
            "
          >
            <hr />
            <ComputeTaskFilesList
              :files="rootTask.results.files"
              @file-preview-requested="
                (file) => filePreviewRequested(rootTask, file)
              "
              @file-download-requested="
                (file) => fileDownloadRequested(rootTask, file)
              "
              :class="
                rootTask.results.files ? ['p-2', 'border', 'rounded'] : []
              "
            />
          </div>
        </div>

        <!-- List Subtasks -->
        <hr />
        <div class="tasks-grid" :style="`--col-min:${colMinWidth}px`">
          <template
            v-for="(subtask, subIdx) in rootTask.subtasks"
            :key="subIdx"
          >
            <div v-if="subtask">
              <TaskResultState
                v-if="getExpanded(subtask.id)"
                :error="detailErrors[subtask.id!]"
              />
              <ComputeTaskDetail
                :id="`task-${subtask.id}`"
                :task="subtask"
                :expanded="getExpanded(subtask.id)"
                :selected-tab="getSelectedTab(subtask.id)"
                @update:expanded="val => setExpanded(subtask.id!, val)"
                @update:selected-tab="val => setSelectedTab(subtask.id!, val)"
                @layout-needed="onLayoutNeeded"
                @bucket-requested="bucketRequested(subtask.bucket, idx, $event)"
                @exec-details-requested="showExecDetails(subtask)"
                @debug-details-requested="debugTaskId = subtask.id || null"
                @abort-requested="taskAbortRequested(subtask)"
                @file-preview-requested="
                  (file) => filePreviewRequested(subtask, file)
                "
                @file-download-requested="
                  (file) => fileDownloadRequested(subtask, file)
                "
              />
            </div>
          </template>
        </div>
      </BCard>
    </template>

    <!-- Paging (below) -->
    <BPagination
      v-if="pagingItemsPerPage > 0 && pagerTotalRows > props.pagingItemsPerPage"
      v-model="pagingPageCurrent"
      :total-rows="pagerTotalRows"
      :per-page="pagingItemsPerPage"
      aria-label="Legacy task pages"
      pills
      size="sm"
    />

    <ComputeTaskDebugDetails
      v-if="debugTaskId"
      :key="debugTaskId"
      :task-id="debugTaskId"
      :project-id="props.projectId"
      @close="debugTaskId = null"
    />

    <!-- Exec Details Modal -->
    <BModal
      v-model="showDetailsModal"
      :class="[detailsContent ? 'modal-xl' : 'modal-sm']"
      @close="
        showDetailsModal = false;
        detailsContent = null;
      "
    >
      <template #header>
        <h2>
          <font-awesome-icon icon="sliders" class="me-2" />
          {{ detailsModalTitle }}
        </h2>
      </template>
      <div>
        <pre
          v-if="detailsContent"
          class="code-highlight"
          v-html="highlightJson(detailsContent)"
        ></pre>
        <p v-else>
          <font-awesome-icon icon="exclamation-triangle" />
          <strong>No Details</strong>
        </p>
      </div>
      <!-- Modal footer with close button only -->
      <template #footer>
        <b-button
          variant="secondary"
          @click="
            showDetailsModal = false;
            detailsContent = null;
          "
        >
          Close
        </b-button>
      </template>
    </BModal>

    <!-- MODAL: Data Object Detail (file-only) -->
    <div
      v-if="
        selectedDataObject &&
        selectedTask?.bucket?.name &&
        selectedTask?.bucket?.id
      "
    >
      <DataObjectDetail
        :show="showModalDataObjectDetail"
        :data-object="selectedDataObject"
        :bucket-name="selectedTask.bucket.name"
        :bucket-id="selectedTask.bucket.id"
        :allow-download="true"
        @close="
          showModalDataObjectDetail = false;
          selectedDataObject = null;
        "
      />
    </div>

    <!-- MODAL: Abort Compute Task -->
    <ComputeTaskAbort
      v-if="showConfirmAbortModal && selectedTask?.id"
      :show="showConfirmAbortModal"
      :task="selectedTask"
      @close="closeTaskAbortModal"
      @close-and-refresh="closeTaskAbortModal"
    />
  </div>
</template>
<style scoped>
.root-task-card {
  h2 {
    font-size: 1rem;
    font-weight: bold;
  }
}

/* grid layout with auto-fit and min column width */
.tasks-grid {
  /* Responsive columns: as many as fit, min width = --col-min */
  display: grid;
  gap: 12px; /* card gutter */
  align-items: start; /* keep tops aligned */
  grid-template-columns: repeat(
    auto-fit,
    minmax(min(100%, var(--col-min, 450px)), 1fr)
  );
}

/* freeze transitions while many collapses toggle at once */
.task-no-anim :is(.collapse, .collapsing, .card, .card-body) {
  transition: none !important;
}
</style>
